import React from 'react';
import { toast } from 'sonner';

import { linkGoogleCalendar, linkOutlookCalendar, descargarICS } from '../../lib/calendario';
import './BotonesDeCalendario.css';

/**
 * "Agregalo a tu calendario", con los tres destinos que cubren casi todo.
 *
 * Vive acá y no dentro de BusinessPage.jsx —que ya tiene 2.3k líneas— porque el
 * bloque son tres botones, tres logos y la lógica de cada destino.
 *
 * Google y Outlook van por link a su formulario de evento nuevo, precargado:
 * no descargan nada ni piden permisos, y quien ya tiene la sesión abierta lo
 * guarda de un click. Apple no tiene una pantalla web equivalente, así que va
 * por .ics, que es justamente lo que abre Calendario en Mac y iPhone. Ese mismo
 * .ics sirve para Thunderbird y para el Outlook de escritorio, y es el que ya
 * viaja adjunto en el mail de confirmación.
 *
 * Los logos son los de cada marca y no un ícono genérico de calendario: en una
 * fila de tres botones, el logo es lo que se lee antes que el texto. Van como
 * SVG inline porque el CSP de la app no permite traerlos de un CDN.
 */

/** Logo de Google Calendar. */
function LogoGoogleCalendar() {
  return (
    <svg viewBox="0 0 200 200" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#fff" d="M148.882 43.618l-47.368-5.263-57.895 5.263L38.355 96l5.264 52.382 52.632 6.579 52.631-6.579L154.145 96z" />
      <path fill="#1a73e8" d="M65.211 125.276c-3.934-2.658-6.658-6.539-8.145-11.671l9.132-3.763c.829 3.158 2.276 5.605 4.342 7.342 2.053 1.737 4.553 2.592 7.474 2.592 2.987 0 5.553-.908 7.697-2.724s3.224-4.132 3.224-6.934c0-2.868-1.132-5.211-3.395-7.026s-5.105-2.724-8.5-2.724h-5.276v-9.039h4.737c2.921 0 5.382-.789 7.382-2.368 2-1.579 3-3.737 3-6.474 0-2.434-.888-4.276-2.664-5.539s-4.026-1.895-6.75-1.895c-2.658 0-4.895.658-6.711 1.987s-3.132 2.987-3.987 4.987l-9.039-3.763c1.395-3.974 3.974-7.474 7.75-10.487s8.605-4.526 14.474-4.526c4.342 0 8.25.836 11.711 2.526 3.461 1.684 6.184 4.026 8.145 7.013 1.961 2.987 2.934 6.342 2.934 10.079 0 3.816-.921 7.039-2.763 9.684-1.842 2.645-4.105 4.671-6.789 6.092v.539a20.66 20.66 0 018.711 6.803c2.276 3.026 3.421 6.645 3.421 10.868 0 4.224-1.079 7.987-3.224 11.303-2.145 3.316-5.105 5.921-8.882 7.803-3.789 1.882-8.039 2.842-12.75 2.842-5.474.013-10.487-1.303-14.421-3.961z" />
      <path fill="#1a73e8" d="M121.25 79.961l-10.027 7.25-5.013-7.605 17.987-12.974h6.895v61.197h-9.842z" />
      <path fill="#ea4335" d="M148.882 196.5l47.368-47.368-23.684-10.526-23.684 10.526-10.526 23.684z" />
      <path fill="#34a853" d="M33.882 172.816l10.526 23.684h104.474v-47.368H44.408z" />
      <path fill="#4285f4" d="M12.947 3.5C5.789 3.5 0 9.289 0 16.447v132.685l23.684 10.526 20.724-10.526V43.618h104.474l10.526-20.724L148.882 3.5z" />
      <path fill="#188038" d="M0 149.132v34.421C0 190.711 5.789 196.5 12.947 196.5h34.421v-47.368z" />
      <path fill="#fbbc04" d="M148.882 43.618v105.514h47.368V43.618l-23.684-10.526z" />
      <path fill="#1967d2" d="M196.25 43.618V16.447C196.25 9.289 190.461 3.5 183.303 3.5h-34.421v40.118z" />
    </svg>
  );
}

/** Logo de Outlook. */
function LogoOutlook() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path fill="#0a2767" d="M43.9 24.8c0-.5-.3-1-.7-1.2l-16-9.4a1.9 1.9 0 00-.4-.2 2 2 0 00-1.8 0l-.4.2-16 9.4c-.4.2-.7.7-.7 1.2l8.8 6.4 9.2 5.9 9.2-5.9 8.8-6.4z" />
      <path fill="#0364b8" d="M13.6 13.3h11.2v10.3H13.6z" />
      <path fill="#0078d4" d="M24.8 13.3H36v10.3H24.8z" />
      <path fill="#28a8ea" d="M24.8 23.6H36v10.3H24.8z" />
      <path fill="#0078d4" d="M13.6 23.6h11.2v10.3H13.6z" />
      <path fill="#14447d" d="M43.2 23.6L27.1 33.2l-2.3 1.3-2.3-1.3-16.1-9.6v18.9c0 1.1.9 2 2 2h32.8c1.1 0 2-.9 2-2V23.6z" opacity=".6" />
      <path fill="#0364b8" d="M43.9 24.8v.4L27.3 35.7c-.8.5-1.8.5-2.6 0L8.1 25.2v-.4l.7-.4 16 9.4c.8.5 1.8.5 2.6 0l16-9.4z" />
      <path fill="#0a2767" d="M43.9 25.2L27.3 35.7c-.8.5-1.8.5-2.6 0L8.1 25.2v16.3c0 1.1.9 2 2 2h31.8c1.1 0 2-.9 2-2z" opacity=".5" />
      <path fill="#1490df" d="M43.9 25.2v.4L27.3 36.1c-.8.5-1.8.5-2.6 0L8.1 25.6v-.4l16.6 10.5c.8.5 1.8.5 2.6 0z" />
      <path fill="#28a8ea" d="M43.9 25.6L27.3 36.1c-.8.5-1.8.5-2.6 0L8.1 25.6v15.9c0 1.1.9 2 2 2h31.8c1.1 0 2-.9 2-2z" />
      <path fill="#0078d4" d="M22.6 34.9H6.1c-1 0-1.9-.8-1.9-1.9V15.5c0-1 .8-1.9 1.9-1.9h16.5c1 0 1.9.8 1.9 1.9V33c0 1.1-.8 1.9-1.9 1.9z" />
      <path fill="#fff" d="M14.4 19.3c-1.4 0-2.5.5-3.3 1.5-.8 1-1.2 2.4-1.2 4.1 0 1.6.4 2.9 1.2 3.9.8 1 1.9 1.5 3.2 1.5 1.4 0 2.4-.5 3.2-1.5.8-1 1.2-2.4 1.2-4.1 0-1.7-.4-3.1-1.1-4-.8-.9-1.8-1.4-3.2-1.4zm-.1 9c-.7 0-1.3-.3-1.7-1-.4-.6-.6-1.5-.6-2.5s.2-1.9.6-2.6c.4-.7 1-1 1.7-1 .8 0 1.3.3 1.7 1 .4.6.6 1.5.6 2.6 0 1.1-.2 2-.6 2.6-.4.6-.9.9-1.7.9z" />
    </svg>
  );
}

/** Logo de Apple. */
function LogoApple() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.61-1.7-3.18-1.73-1.35-.14-2.64.79-3.33.79-.69 0-1.75-.77-2.87-.75-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.3-3.5zM14.88 5.6c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.06 1.7-.93 2.7.97.08 1.97-.49 2.59-1.23z"
      />
    </svg>
  );
}

export default function BotonesDeCalendario({ evento, nombreArchivo = 'turno.ics' }) {
  if (!evento?.fechaISO || !evento?.hora) return null;

  const descargar = () => {
    descargarICS(evento, nombreArchivo);
    toast.success('Archivo descargado', {
      description: 'Abrilo para agregar el turno a tu calendario.',
    });
  };

  return (
    <div className="botones-calendario">
      <a
        href={linkGoogleCalendar(evento)}
        target="_blank"
        rel="noopener noreferrer"
        className="boton-calendario"
      >
        <LogoGoogleCalendar />
        Google
      </a>

      <a
        href={linkOutlookCalendar(evento)}
        target="_blank"
        rel="noopener noreferrer"
        className="boton-calendario"
      >
        <LogoOutlook />
        Outlook
      </a>

      {/* Apple va por descarga, no por link: el .ics es el formato que abre
          Calendario en Mac y iPhone. El mismo archivo sirve para Thunderbird y
          para el Outlook de escritorio. */}
      <button type="button" className="boton-calendario" onClick={descargar}>
        <LogoApple />
        Apple
      </button>
    </div>
  );
}
